import { finalize } from "rxjs";
import { Auth0Service } from "../service/auth0.service";
import { getCookie, getTokenExpiry, setCookieInMinutes } from "../../data/utils/cookie-utils";
import { sessionExpired } from "./token-polling.utils";

export const refreshIntervals: Record<string, any> = {};
export const refreshingMap: Record<string, boolean> = {};

const GLOBAL_KEY = "global_lastActivityTime";
const INTERVAL_KEY = "global";

// ---------- Activity Tracker ----------
export function markUserActivity() {
  try {
    sessionStorage.setItem(GLOBAL_KEY, Date.now().toString());
  } catch {}
}

export function initActivityTracker() {
  if ((window as any).__activityTrackerStarted) return;
  (window as any).__activityTrackerStarted = true;

  const updateActivity = () => markUserActivity();

  ["click", "touchstart"].forEach((evt) => {
    window.addEventListener(evt, updateActivity, { passive: true } as any);
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) updateActivity();
  });

  updateActivity();
}

// ---------- Stop Checker ----------
export function stopTokenRefreshChecker() {
  if (refreshIntervals[INTERVAL_KEY]) {
    clearInterval(refreshIntervals[INTERVAL_KEY]);
    delete refreshIntervals[INTERVAL_KEY];
  }
  delete refreshingMap[INTERVAL_KEY];
}

// ---------- Token Check ----------
export function checkAndRefreshToken(
  auth: Auth0Service,
  refreshThresholdMs: number,
  accessTokenExpiryHours: number
) {
  const accessToken = getCookie(`access_token`);
  const refreshToken = getCookie(`refresh_token`);

  if (!accessToken || !refreshToken) {
    console.log(`[Token] 🚫 Missing tokens.`);
    sessionExpired();
    stopTokenRefreshChecker();
    return;
  }

  const expTimeMs = getTokenExpiry(accessToken);
  const remainingMs = expTimeMs ? expTimeMs - Date.now() : 0;
  const remainingMin = Math.max(0, Math.floor(remainingMs / 60000));

  console.log(`[Token] 🔍 ${remainingMin}m remaining before expiry.`);

  if (remainingMs <= 0) {
    console.log(`[Token] ❌ Token expired → Ending session.`);
    sessionExpired();
    stopTokenRefreshChecker();
    return;
  }

  if (remainingMs <= refreshThresholdMs) {
    console.log(`[Token] ⚠ Token nearing expiry (${remainingMin}m left) → Refreshing...`);
    performTokenRefresh(auth, refreshToken, accessTokenExpiryHours);
  } else {
    console.log(`[Token] 🔒 Token valid (${remainingMin}m left).`);
  }
}

// ---------- Refresh Logic ----------
export function performTokenRefresh(
  auth: Auth0Service,
  refreshToken: string,
  accessTokenExpiryHours: number
) {
  if (refreshingMap[INTERVAL_KEY]) return;
  refreshingMap[INTERVAL_KEY] = true;

  auth
    .refreshToken(refreshToken)
    .pipe(finalize(() => (refreshingMap[INTERVAL_KEY] = false)))
    .subscribe({
      next: (response: any) => {
        const newAccessToken = response?.access_token;
        const newRefreshToken = response?.refresh_token;

        if (newAccessToken) {
          const expTimeMs = getTokenExpiry(newAccessToken);
          const ttlMinutes = expTimeMs
            ? Math.max(1, Math.floor((expTimeMs - Date.now()) / (60 * 1000)))
            : accessTokenExpiryHours * 60;

          setCookieInMinutes(`access_token`, newAccessToken, ttlMinutes);
          if (newRefreshToken) {
            setCookieInMinutes(`refresh_token`, newRefreshToken, 4 * 60);
          }

          const issuedAtIST = new Date().toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          });

          console.log(`[Token] ✅ Refreshed → New expiry: ${ttlMinutes}m. Issued at: ${issuedAtIST}.`);
        } else {
          console.log(`[Token] ❌ Invalid refresh response → Session expired.`);
          sessionExpired();
        }
      },
      error: (err) => {
        console.error(`[Token] ❌ Refresh failed:`, err);
        sessionExpired();
      },
    });
}